/**
 * TrackOnboardingCard — calm 3-option pupil setup card.
 *
 * Production regression fix (May 14, 2026):
 *   The previous 2-button card asked "Do you currently have an
 *   instructor?" with Yes / No only. Pupils who wanted help finding an
 *   instructor had no obvious path. This card now surfaces three
 *   explicit options:
 *     1. I already have an instructor      → POST /api/tracks/set
 *                                             track=instructor_standard
 *     2. Find me an instructor             → /learn/connect
 *     3. I'm learning independently for now → POST /api/tracks/set
 *                                             track=independent
 *
 * Visibility rules:
 *   • Only renders when /api/tracks/me returns
 *     `track_explicitly_set === false` AND `anonymous === false`
 *     AND viewer is a learner (LearnerHub gates on user.role).
 *
 * Styling:
 *   • Calm white surface to match the new P2-lite dashboard pattern.
 *     No dark gradient slab.
 */
import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus, GraduationCap, Search, Loader2, ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;


export default function TrackOnboardingCard({ onAnswered }) {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(null);
  // null | "have" | "find" | "indep"

  const setTrack = async (track, hasInstructor) => {
    try {
      await axios.post(
        `${API}/api/tracks/set`,
        { track, has_instructor: hasInstructor },
        { withCredentials: true },
      );
      onAnswered?.(track);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail
          || "Could not save your answer. Please try again.",
      );
    }
  };

  const onHaveInstructor = async () => {
    setSubmitting("have");
    await setTrack("instructor_standard", true);
    setSubmitting(null);
  };

  const onFindInstructor = async () => {
    // Set track first (so they're not stuck in onboarding limbo),
    // then route to the discovery flow. We also record an explicit
    // `find_instructor` intent so the backend can start a 7-day
    // grace timer — if the pupil never finishes matching, a calm
    // "Still looking?" recovery banner surfaces on /learn after the
    // window elapses (StillLookingInstructorBanner).
    setSubmitting("find");
    try {
      await axios.post(
        `${API}/api/tracks/set`,
        {
          track: "instructor_standard",
          has_instructor: true,
          intent: "find_instructor",
        },
        { withCredentials: true },
      );
      onAnswered?.("instructor_standard");
    } catch (e) {
      toast.error(
        e?.response?.data?.detail
          || "Could not save your answer. Please try again.",
      );
    }
    setSubmitting(null);
    navigate("/learn/connect");
  };

  const onIndependent = async () => {
    setSubmitting("indep");
    await setTrack("independent", false);
    setSubmitting(null);
  };

  return (
    <section
      data-testid="track-onboarding-card"
      className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 sm:p-5 shadow-sm"
    >
      <div>
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 dark:text-slate-400">
          Quick one before we start
        </p>
        <h3
          data-testid="track-onboarding-question"
          className="text-base sm:text-lg font-extrabold text-slate-900 dark:text-white leading-tight mt-1"
        >
          How are you learning?
        </h3>
        <p className="text-[12px] text-slate-500 dark:text-slate-400 mt-1.5 leading-relaxed">
          This helps us tailor your home screen. You can change this any time.
        </p>
      </div>

      <div className="mt-4 space-y-2">
        <OptionButton
          testid="track-onboarding-have"
          icon={GraduationCap}
          iconBg="bg-emerald-50 dark:bg-emerald-900/30"
          iconColor="text-emerald-600 dark:text-emerald-300"
          title="I already have an instructor"
          subtitle="Use my dashboard to track lessons and progress"
          loading={submitting === "have"}
          disabled={submitting !== null}
          onClick={onHaveInstructor}
        />
        <OptionButton
          testid="track-onboarding-find"
          icon={Search}
          iconBg="bg-indigo-50 dark:bg-indigo-900/30"
          iconColor="text-indigo-600 dark:text-indigo-300"
          title="Find me an instructor"
          subtitle="See instructors near you and request a match"
          loading={submitting === "find"}
          disabled={submitting !== null}
          onClick={onFindInstructor}
        />
        <OptionButton
          testid="track-onboarding-indep"
          icon={UserPlus}
          iconBg="bg-slate-100 dark:bg-slate-800"
          iconColor="text-slate-600 dark:text-slate-300"
          title="I'm learning independently for now"
          subtitle="Track progress yourself and connect later if you want"
          loading={submitting === "indep"}
          disabled={submitting !== null}
          onClick={onIndependent}
        />
      </div>
    </section>
  );
}

function OptionButton({
  testid, icon: Icon, iconBg, iconColor, title, subtitle,
  loading, disabled, onClick,
}) {
  return (
    <button
      type="button"
      data-testid={testid}
      disabled={disabled}
      onClick={onClick}
      className="w-full text-left rounded-xl border border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 disabled:opacity-60 disabled:cursor-not-allowed p-3 sm:p-4 flex items-center gap-3 transition-colors group"
    >
      <div className={`w-10 h-10 rounded-xl ${iconBg} flex items-center justify-center flex-shrink-0`}>
        {loading ? (
          <Loader2 className={`w-5 h-5 animate-spin ${iconColor}`} />
        ) : (
          <Icon className={`w-5 h-5 ${iconColor}`} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-extrabold text-slate-900 dark:text-white">
          {title}
        </p>
        <p className="text-[12px] text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed">
          {subtitle}
        </p>
      </div>
      <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0 group-hover:translate-x-0.5 transition-transform" />
    </button>
  );
}
